import { supabase } from '@/integrations/supabase/client';
import {
  DashboardItinerary,
  DashboardTravelRequest,
  type IDashboardViewData,
  type IDashboardTravelRequestData,
} from '@/lib/models/dashboard';
import type {
  DashboardItinerariesQuery,
  DashboardItineraryCountRow,
  DashboardItineraryFilterOptionRow,
  DashboardItineraryPageExtra,
  DashboardItineraryPhase,
  DashboardItineraryTab,
  DashboardTravelRequestCountRow,
  DashboardTravelRequestFilter,
  DashboardTravelRequestPageExtra,
  DashboardTravelRequestPhase,
  DashboardTravelRequestsQuery,
  DashboardTravelRequestView,
} from '@/integrations/supabase/rpc-types';

export interface DashboardPage<T> {
  rows: T[];
  /** Righe della lista filtrata, prima della paginazione. */
  totalCount: number;
}

/** Totale per vista (badge) e per sotto-stato della vista active (pillole). */
export interface DashboardTravelRequestCounts {
  byView: Partial<Record<DashboardTravelRequestView, number>>;
  byPhase: Partial<Record<DashboardTravelRequestPhase, number>>;
}

export interface DashboardTravelRequestPage extends DashboardPage<DashboardTravelRequest> {
  /** Delle righe filtrate, quante create nelle ultime 24 ore. */
  newCount: number;
}

export interface DashboardItineraryCounts {
  /** Totale per tab; le tab assenti valgono zero. */
  byTab: Partial<Record<DashboardItineraryTab, number>>;
  /** Totale per sotto-stato dentro ogni tab; i sotto-stati assenti valgono zero. */
  byPhase: Partial<Record<DashboardItineraryTab, Partial<Record<DashboardItineraryPhase, number>>>>;
}

export interface DashboardFilterOption {
  value: string;
  label: string;
  email: string | null;
}

/**
 * Liste della dashboard viaggi e della pagina richieste. Due famiglie di RPC separate:
 * gli itinerari arrivano con la loro travel request in join, le richieste da sole.
 * Tab, filtri, ordinamento e paginazione li fa il database.
 */
export class DashboardListsService {
  // ── Itinerari ──────────────────────────────────────────────────────────

  static async getItinerariesPage(query: DashboardItinerariesQuery): Promise<DashboardPage<DashboardItinerary>> {
    const { data, error } = await supabase.rpc('get_dashboard_itineraries_page' as any, {
      p_tab: query.tab,
      p_phase: query.phase ?? null,
      p_designer_id: query.designerId ?? null,
      p_client_email: query.clientEmail ?? null,
      p_limit: query.limit,
      p_offset: query.offset,
      p_itinerary_id: query.itineraryId ?? null,
    });
    if (error) throw new Error(`Errore nel caricamento degli itinerari: ${error.message}`);

    const rows = (data ?? []) as unknown as Array<IDashboardViewData & DashboardItineraryPageExtra>;
    return {
      rows: rows.map((row) => new DashboardItinerary(row)),
      totalCount: Number(rows[0]?.total_count ?? 0),
    };
  }

  /**
   * Un solo itinerario visibile all'utente, nella forma della dashboard: serve
   * all'anteprima dell'itinerario scelto dalla pagina richieste.
   */
  static async getItinerary(itineraryId: string): Promise<DashboardItinerary | null> {
    const page = await this.getItinerariesPage({ tab: null, itineraryId, limit: 1, offset: 0 });
    return page.rows[0] ?? null;
  }

  static async getItineraryCounts(
    filters: { designerId?: string | null; clientEmail?: string | null } = {}
  ): Promise<DashboardItineraryCounts> {
    const { data, error } = await supabase.rpc('get_dashboard_itinerary_counts' as any, {
      p_designer_id: filters.designerId ?? null,
      p_client_email: filters.clientEmail ?? null,
    });
    if (error) throw new Error(`Errore nel caricamento dei contatori itinerari: ${error.message}`);

    const result: DashboardItineraryCounts = { byTab: {}, byPhase: {} };
    ((data ?? []) as unknown as DashboardItineraryCountRow[]).forEach((row) => {
      const total = Number(row.total);
      result.byTab[row.tab] = (result.byTab[row.tab] ?? 0) + total;
      if (row.phase) {
        result.byPhase[row.tab] = { ...result.byPhase[row.tab], [row.phase]: total };
      }
    });
    return result;
  }

  static async getItineraryFilterOptions(
    tab: DashboardItineraryTab
  ): Promise<{ designers: DashboardFilterOption[]; clients: DashboardFilterOption[] }> {
    const { data, error } = await supabase.rpc('get_dashboard_itinerary_filter_options' as any, {
      p_tab: tab,
    });
    if (error) throw new Error(`Errore nel caricamento dei filtri: ${error.message}`);

    const toOption = (row: DashboardItineraryFilterOptionRow): DashboardFilterOption => ({
      value: row.value,
      label: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.email || row.value,
      email: row.email,
    });
    const byLabel = (a: DashboardFilterOption, b: DashboardFilterOption) => a.label.localeCompare(b.label);
    const rows = (data ?? []) as unknown as DashboardItineraryFilterOptionRow[];

    return {
      designers: rows.filter((r) => r.kind === 'designer').map(toOption).sort(byLabel),
      clients: rows.filter((r) => r.kind === 'client').map(toOption).sort(byLabel),
    };
  }

  // ── Travel request ─────────────────────────────────────────────────────

  static async getTravelRequestsPage(query: DashboardTravelRequestsQuery): Promise<DashboardTravelRequestPage> {
    const { data, error } = await supabase.rpc('get_dashboard_travel_requests_page' as any, {
      p_view: query.view,
      p_phase: query.phase ?? null,
      p_filters: query.filters ?? [],
      p_sort: query.sort ?? 'newest',
      p_limit: query.limit,
      p_offset: query.offset,
      p_request_id: query.requestId ?? null,
    });
    if (error) throw new Error(`Errore nel caricamento delle richieste: ${error.message}`);

    const rows = (data ?? []) as unknown as Array<IDashboardTravelRequestData & DashboardTravelRequestPageExtra>;
    return {
      rows: rows.map((row) => new DashboardTravelRequest(row)),
      totalCount: Number(rows[0]?.total_count ?? 0),
      newCount: Number(rows[0]?.new_count ?? 0),
    };
  }

  /** Una sola richiesta visibile all'utente, per il deep link `?requestId=`. */
  static async getTravelRequest(requestId: string): Promise<DashboardTravelRequest | null> {
    const page = await this.getTravelRequestsPage({ view: null, requestId, limit: 1, offset: 0 });
    return page.rows[0] ?? null;
  }

  static async getTravelRequestCounts(
    filters: DashboardTravelRequestFilter[] = []
  ): Promise<DashboardTravelRequestCounts> {
    const { data, error } = await supabase.rpc('get_dashboard_travel_request_counts' as any, {
      p_filters: filters,
    });
    if (error) throw new Error(`Errore nel caricamento dei contatori richieste: ${error.message}`);

    const result: DashboardTravelRequestCounts = { byView: {}, byPhase: {} };
    ((data ?? []) as unknown as DashboardTravelRequestCountRow[]).forEach((row) => {
      const total = Number(row.total);
      // 'new' e 'imminent' ricontano richieste gia' nei sotto-stati: fuori dal totale della vista.
      if (row.phase !== 'new' && row.phase !== 'imminent') result.byView[row.view] = (result.byView[row.view] ?? 0) + total;
      if (row.phase) result.byPhase[row.phase] = total;
    });
    return result;
  }

}
